import { FC, useEffect, ReactNode } from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import { useIntl, defineMessages } from 'dibs-react-intl';

import { SingleSelect, SingleOnChangeArgs } from 'dibs-elements/exports/SingleSelect';

import { VerticalType } from '../SellerApplicationFormReducer';

import { ItemCondition_viewer$key } from './__generated__/ItemCondition_viewer.graphql';
import {
    ItemConditionRefetchQuery,
    VerticalType as QueryVerticalType,
} from './__generated__/ItemConditionRefetchQuery.graphql';

const verticalsMap: { [key: string]: QueryVerticalType } = {
    Furniture: 'furniture',
    Art: 'art',
    Fashion: 'fashion',
    Jewelry: 'jewelry',
};

const messages = defineMessages({
    itemConditionPlaceholder: {
        id: 'SellerApplicationForm.ItemCondition.itemConditionPlaceholder',
        defaultMessage: 'Item Condition',
    },
});

type Props = {
    viewer: ItemCondition_viewer$key;
    vertical: VerticalType | null;
    onChange: (e: SingleOnChangeArgs) => void;
    value: string;
    disabled: boolean;
    errorMessage: ReactNode;
};

const ItemCondition: FC<Props> = ({
    vertical,
    viewer: viewerRef,
    onChange,
    value,
    disabled,
    errorMessage,
}) => {
    const intl = useIntl();

    const [data, refetch] = useRefetchableFragment<
        ItemConditionRefetchQuery,
        ItemCondition_viewer$key
    >(
        graphql`
            fragment ItemCondition_viewer on Viewer
            @argumentDefinitions(vertical: { type: "VerticalType" })
            @refetchable(queryName: "ItemConditionRefetchQuery") {
                itemConditionsList(vertical: $vertical) {
                    title
                    description
                }
            }
        `,
        viewerRef
    );

    useEffect(() => {
        refetch({ vertical: verticalsMap[vertical || ''] });
    }, [vertical, refetch]);

    const options = (data?.itemConditionsList || []).map(c => ({
        label: c?.title || '',
        value: c?.title || '',
        customDropdownDisplay: (
            <>
                <b>{c?.title || ''}:</b> {c?.description || ''}
            </>
        ),
    }));

    return (
        <SingleSelect
            dataTn="item-condition-select"
            name="item-condition-select"
            onChange={onChange}
            placeholder={intl.formatMessage(messages.itemConditionPlaceholder)}
            options={options}
            value={value}
            clearable={false}
            disabled={disabled}
            errorMessage={errorMessage}
            hasError={!!errorMessage}
        />
    );
};

export default ItemCondition;
